import React, { useRef, useState, useCallback } from 'react';

/**
 * Lightweight, dependency-free 3D tilt wrapper.
 * Follows the cursor to rotate the card slightly on X/Y axes,
 * adds a soft moving light reflection for depth.
 */
const TiltCard = ({ children, className = '', max = 8, glare = true, ...props }) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({});
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50, opacity: 0 });

  const handleMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const rotateY = (px - 0.5) * (max * 2);
    const rotateX = (0.5 - py) * (max * 2);
    setStyle({
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-6px) scale(1.02)`,
    });
    if (glare) setGlarePos({ x: px * 100, y: py * 100, opacity: 0.18 });
  }, [max, glare]);

  const handleMouseLeave = useCallback(() => {
    setStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0) scale(1)',
    });
    setGlarePos((g) => ({ ...g, opacity: 0 }));
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative transition-transform duration-200 ease-out will-change-transform [transform-style:preserve-3d] ${className}`}
      style={style}
      {...props}
    >
      {children}
      {glare && (
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-200"
          style={{
            background: `radial-gradient(circle at ${glarePos.x}% ${glarePos.y}%, rgba(251,146,60,0.35), transparent 55%)`,
            opacity: glarePos.opacity,
            mixBlendMode: 'screen',
          }}
        />
      )}
    </div>
  );
};

export default TiltCard;
